import noop from 'utils/noop';
import { AND, OR, NOT } from 'utils/combinatorTypes';
import { IS_SET } from 'utils/comparatorTypes';
import {
  CUSTOM_EVENT_SYSTEM_FIELDS,
  FilterInputsList,
} from 'modules/journey/components/FilterInputsList';
import {
  Combinator,
  Query,
} from 'modules/journey/components/FilterInputsList/FilterInputsList.types';
import { SegmentedControl } from 'ui/segmentedControl';
import { Subtitle2 } from 'ui/theme';
import { useMemo } from 'react';
import { FormSectionContent } from '../../../../EditorBody.styled';
import { Sub2Span, GrayCaption, CombinatorContainer } from './styled';

const content = {
  header: 'Additional Entry Rules',
  headerCaption: 'Get more specific about who should enter',
  combinatorPrefix: 'Must have ',
  combinatorSuffix: ' of the following:',
};

const combinatorText = {
  [AND]: { conjunction: 'and', name: 'All', value: AND },
  [OR]: { conjunction: 'or', name: 'Any', value: OR },
  [NOT]: { conjunction: 'nor', name: 'None', value: NOT },
};

type FiltersInputProps = {
  combinator: Combinator;
  dataType: string;
  onCombinatorUpdate: (combinator: Combinator) => void;
  onSearchQueriesUpdate: (queries: Partial<Query>[]) => void;
  queries: Partial<Query>[];
  initialQueries: Query[];
  trigger: string;
  selectedResourceName?: string;
  isReadOnly?: boolean;
};

const isSearchQueriesOnlyForAllowed = (eventName: string, searchQueries: Partial<Query>[] = []) => {
  const getIndex = (item: string, q?: string) => (q ? q?.indexOf(item) : -1);
  for (let i = 0; i < searchQueries.length; i++) {
    const query = searchQueries[i];
    if (!query.field) break; // search query doesn't contain a field? skip the for loop iteration (should never happen, but TypeScript says it might)
    if (!CUSTOM_EVENT_SYSTEM_FIELDS.has(query.field) && !(getIndex(eventName, query.field) > -1)) {
      return false;
    }
  }
  return true;
};

/**
 * NOTE: No longer used after StartTileRefresh is GA
 */
export const FiltersInput = ({
  combinator,
  dataType,
  onCombinatorUpdate = noop,
  onSearchQueriesUpdate = noop,
  queries = [],
  trigger,
  selectedResourceName,
  isReadOnly,
  ...props
}: FiltersInputProps) => {
  const onSearchQueriesUpdateWithActiveFilters = (newQueries: Partial<Query>[]) => {
    // we can't save when comparator type is IS_SET because the value is null
    // backend expects an empty string as value, so we manually add it
    const saveableNewQueries = newQueries.map((q) =>
      q.comparatorType === IS_SET ? { ...q, value: '' } : q,
    );

    onSearchQueriesUpdate(saveableNewQueries);
  };
  const filteredSearchQueries = useMemo(() => {
    const shouldFilterQueries = dataType === 'customEvent' && !!selectedResourceName;
    const queriesAreLoaded = queries?.[0]?.field && queries?.[queries.length - 1]?.field;
    if (shouldFilterQueries && queriesAreLoaded) {
      return isSearchQueriesOnlyForAllowed(selectedResourceName, queries) ? queries : [];
    } else {
      return queries;
    }
  }, [queries, dataType, selectedResourceName]);

  const onSelect = (selected: Combinator) => onCombinatorUpdate(selected);
  return (
    <FormSectionContent data-test="filters-input">
      <Sub2Span>{content.header}</Sub2Span>
      <br />
      <GrayCaption>{content.headerCaption}</GrayCaption>

      {/* only show combinator selector if there is at least one query added */}
      {queries?.length > 0 && (
        <CombinatorContainer>
          <Subtitle2>{content.combinatorPrefix}</Subtitle2>
          <SegmentedControl
            data-test="filters-input-combinator-input"
            onSelect={onSelect}
            options={Object.values(combinatorText).map((opt) => ({
              ...opt,
              disabled: isReadOnly,
            }))}
            value={combinator || AND}
          />
          <Subtitle2>{content.combinatorSuffix}</Subtitle2>
        </CombinatorContainer>
      )}
      <FilterInputsList
        combinatorText={combinatorText[combinator].conjunction}
        dataType={dataType}
        onSearchQueriesUpdate={onSearchQueriesUpdateWithActiveFilters}
        queries={filteredSearchQueries}
        trigger={trigger}
        propertyMapObjectFilterString={
          dataType === 'customEvent' ? selectedResourceName : undefined
        }
        isDisabled={isReadOnly}
        canAddFilter={!isReadOnly}
        canDelete={!isReadOnly}
        {...props}
      />
    </FormSectionContent>
  );
};
